import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { GraduationCap, Plus, Lock, Calendar } from 'lucide-react';
import { useHeroImage } from '@/hooks/useHeroImage';
import PageIcon from '@/components/PageIcon';
import PageQuote from '@/components/PageQuote';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudyBuddy() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const heroImage = useHeroImage('study_buddy');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statusRes, coursesRes] = await Promise.all([
        axios.get(`${API}/study-buddy/subscription-status`),
        axios.get(`${API}/study-buddy/courses`)
      ]);
      
      setSubscriptionStatus(statusRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load Study Buddy data');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (index) => {
    // Card 0 is always available for free users
    if (index === 0 || subscriptionStatus?.has_subscription) {
      // If course exists, navigate to dashboard
      if (courses[index]) {
        navigate(`/study-buddy/course/${courses[index].id}`);
      } else {
        // Open create modal
        setShowCreateModal(true);
      }
    } else {
      // Redirect to subscription page
      navigate('/study-buddy/subscribe');
    }
  };

  const renderCard = (index) => {
    const course = courses[index];
    const isLocked = index > 0 && !subscriptionStatus?.has_subscription;
    const isEmpty = !course;

    if (isLocked) {
      return (
        <div
          key={index}
          className="cassette-card relative overflow-hidden opacity-50 cursor-not-allowed"
          onClick={() => handleCardClick(index)}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 z-10 flex items-center justify-center">
            <div className="text-center">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-white text-sm font-bold">Subscribe to Unlock</p>
            </div>
          </div>
          <div className="p-8 text-center">
            <div className="h-40"></div>
          </div>
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div
          key={index}
          className="cassette-card relative overflow-hidden hover:shadow-2xl transition-all cursor-pointer group"
          onClick={() => handleCardClick(index)}
        >
          <div className="p-8 text-center">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-[#E8934D] group-hover:text-[#E85D4D] transition-colors" />
            <h3 className="text-2xl retro-title mb-3 text-[#2C1810]">Create Schedule</h3>
            <p className="text-gray-700 mb-6">Click to add your college course</p>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#E8934D] text-white rounded-lg group-hover:bg-[#E85D4D] transition-colors">
              <Plus className="w-5 h-5" />
              <span className="font-bold">Add Course</span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={index}
        className="cassette-card relative overflow-hidden hover:shadow-2xl transition-all cursor-pointer group"
        onClick={() => handleCardClick(index)}
      >
        <div className="p-6">
          <div className="mb-4">
            <GraduationCap className="w-10 h-10 text-[#E8934D] group-hover:text-[#E85D4D] transition-colors" />
          </div>
          <h3 className="text-lg font-bold text-[#2C1810] mb-2">{course.school_name}</h3>
          <h4 className="text-xl retro-title text-[#E85D4D] mb-3">{course.course_name}</h4>
          {course.course_code && (
            <p className="text-sm text-gray-600 mb-2">{course.course_code}</p>
          )}
          <div className="mt-4 pt-4 border-t border-gray-300">
            <p className="text-sm text-gray-500">Click to view dashboard</p>
          </div>
        </div>
      </div>
    );
  };

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <section className="relative">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-[#3D2817] via-[#2C1810] to-[#3D2817] py-20">
          {heroImage && (
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-20"
              style={{ backgroundImage: `url(${heroImage})` }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#E8B67A] to-transparent opacity-30 z-20"></div>
          
          <div className="relative z-30 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <PageIcon pageName="study_buddy" size="lg" className="mx-auto mb-6" />
              <h1 className="text-5xl sm:text-6xl md:text-7xl font-bold retro-title text-[#F4E4C1] mb-6 drop-shadow-[0_4px_8px_rgba(0,0,0,0.5)]">
                Study Buddy
              </h1>
              <p className="text-xl md:text-2xl text-[#F4E4C1] max-w-3xl mx-auto font-medium">
                Organize your college courses, track deadlines, and achieve your academic goals with AI-powered study planning
              </p>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Quote Position 1 - Above Main Content */}
          <PageQuote pageName="study_buddy" position={1} variant="compact" />

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
            </div>
          ) : (
            <>
              {/* Subscription Status Banner */}
              {subscriptionStatus && (
                <div className="mb-8 p-6 bg-gradient-to-r from-[#E8934D]/10 to-[#E85D4D]/10 rounded-lg border-2 border-[#E8934D]">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-black mb-1">
                        {subscriptionStatus.has_subscription ? '✨ Premium Member' : '🎯 Free Trial'}
                      </h3>
                      <p className="text-black mb-2">
                        You can manage up to <span className="font-bold text-[#E85D4D]">{subscriptionStatus.max_courses}</span> college courses
                      </p>
                    </div>
                    {subscriptionStatus.has_subscription ? (
                      <button
                        onClick={() => navigate('/study-buddy/my-courses')}
                        className="px-8 py-4 bg-gradient-to-r from-[#E8934D] to-[#E85D4D] text-white text-lg font-bold rounded-xl hover:from-[#E85D4D] hover:to-[#E8934D] transition-all transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center gap-3"
                      >
                        📊 View My Dashboard
                        <span className="text-2xl">→</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/study-buddy/subscribe')}
                        className="px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors"
                      >
                        Upgrade to Premium
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* 10 Course Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                {[...Array(10)].map((_, index) => renderCard(index))}
              </div>
            </>
          )}

          {/* Quote Position 2 - Below Main Content */}
          <PageQuote pageName="study_buddy" position={2} variant="compact" />
        </div>
      </section>

      {/* Create Course Modal */}
      {showCreateModal && (
        <CreateCourseModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </Layout>
  );
}

// Create Course Modal Component
function CreateCourseModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    school_name: '',
    course_name: '',
    course_code: '',
    instructor_name: '',
    semester: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/study-buddy/courses`, null, {
        params: formData
      });
      toast.success('Course created successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-gradient-to-br from-[#3D2817] via-[#2C1810] to-[#3D2817] max-w-2xl w-full mx-4 p-8 relative max-h-[90vh] overflow-y-auto rounded-lg shadow-2xl border-4 border-[#E8B67A]">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-[#E8B67A] text-2xl font-bold"
        >
          ×
        </button>

        <h2 className="text-3xl retro-title text-white mb-6">Create College Course</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-white font-bold mb-2">School Name *</label>
            <input
              type="text"
              required
              value={formData.school_name}
              onChange={(e) => setFormData({ ...formData, school_name: e.target.value })}
              placeholder="e.g., Pacific College of Health and Science"
              className="w-full px-4 py-3 bg-white/10 border-2 border-[#E8B67A] rounded-lg text-white placeholder-gray-400 focus:border-[#E8934D] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-white font-bold mb-2">Course Name *</label>
            <input
              type="text"
              required
              value={formData.course_name}
              onChange={(e) => setFormData({ ...formData, course_name: e.target.value })}
              placeholder="e.g., Point Location 302"
              className="w-full px-4 py-3 bg-white/10 border-2 border-[#E8B67A] rounded-lg text-white placeholder-gray-400 focus:border-[#E8934D] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-white font-bold mb-2">Course Code</label>
            <input
              type="text"
              value={formData.course_code}
              onChange={(e) => setFormData({ ...formData, course_code: e.target.value })}
              placeholder="e.g., ACUP-302"
              className="w-full px-4 py-3 bg-white/10 border-2 border-[#E8B67A] rounded-lg text-white placeholder-gray-400 focus:border-[#E8934D] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-white font-bold mb-2">Instructor Name</label>
            <input
              type="text"
              value={formData.instructor_name}
              onChange={(e) => setFormData({ ...formData, instructor_name: e.target.value })}
              placeholder="e.g., Dr. Smith"
              className="w-full px-4 py-3 bg-white/10 border-2 border-[#E8B67A] rounded-lg text-white placeholder-gray-400 focus:border-[#E8934D] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-white font-bold mb-2">Semester</label>
            <input
              type="text"
              value={formData.semester}
              onChange={(e) => setFormData({ ...formData, semester: e.target.value })}
              placeholder="e.g., Fall 2025"
              className="w-full px-4 py-3 bg-white/10 border-2 border-[#E8B67A] rounded-lg text-white placeholder-gray-400 focus:border-[#E8934D] focus:outline-none"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-600 text-white font-bold rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Course'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
